import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import {
  CustomButton,
  CustomTextInput,
  CustomTable,
  CustomModal,
  CustomTruncateText,
  type CustomColumnDef,
} from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import { fetchCustomers } from "@/store/customerSlice";
import { type Customer, customerDisplayName } from "@ippon/shared-types";
import { Can } from "@/components/auth/Can";

interface Props {
  open: boolean;
  onClose: () => void;
  /** 行クリックで顧客を確定。呼び出し元で customer_id を契約に紐づける。 */
  onSelect: (customer: Customer) => void;
}

/**
 * 既存顧客から選ぶモーダル (一覧 + 検索 + 行選択)。
 * 物件構成(Unit)登録の「既存顧客から登録」導線で使用 (Phase 2)。
 */
const CustomerSelectModal: React.FC<Props> = ({ open, onClose, onSelect }) => {
  const dispatch = useDispatch<AppDispatch>();
  const customers = useSelector((s: RootState) => s.customers.list.data);
  const loading = useSelector((s: RootState) => s.customers.list.loading);

  const [searchQuery, setSearchQuery] = useState("");
  const { register, getValues, setValue } = useForm<{ q: string }>({
    defaultValues: { q: "" },
  });

  useEffect(() => {
    if (open) {
      dispatch(fetchCustomers(undefined));
      setSearchQuery("");
      setValue("q", "");
    }
  }, [open, dispatch, setValue]);

  const rows = useMemo(
    () =>
      (customers as Customer[])
        .filter((c) => {
          if (!searchQuery) return true;
          const kw = searchQuery.toLowerCase();
          return [
            c.last_name,
            c.first_name,
            c.company_name,
            c.representative_last_name,
            c.representative_first_name,
            c.phone,
            c.email,
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(kw));
        })
        .map((c) => ({
          id: c.customer_id,
          ...c,
          display_name: customerDisplayName(c),
          type_label: c.customer_type === "corporate" ? "法人" : "個人",
        })),
    [customers, searchQuery],
  );

  const columns: CustomColumnDef[] = useMemo(
    () => [
      {
        field: "display_name",
        headerName: "氏名 / 法人名",
        flex: 1.4,
        renderCell: ({ row }) => (
          <Box sx={{ width: "100%", fontSize: "14px" }}>
            <CustomTruncateText
              text={String((row as any).display_name || "-")}
            />
          </Box>
        ),
      },
      {
        field: "type_label",
        headerName: "種別",
        width: 70,
        renderCell: ({ value }) => (
          <Box sx={{ width: "100%", fontSize: "14px" }}>
            {String(value ?? "")}
          </Box>
        ),
      },
      {
        field: "phone",
        headerName: "電話番号",
        flex: 1,
        renderCell: ({ value }) => (
          <Box sx={{ width: "100%", fontSize: "14px" }}>
            <CustomTruncateText text={String(value ?? "")} />
          </Box>
        ),
      },
      {
        field: "_action",
        headerName: "",
        width: 90,
        renderCell: ({ row }) => (
          <Can action="customer:edit">
            <CustomButton
              label="選択"
              onClick={() => {
                onSelect(row as Customer);
                onClose();
              }}
            />
          </Can>
        ),
      },
    ],
    [onSelect, onClose],
  );

  const handleSearch = () => setSearchQuery(getValues().q || "");

  return (
    <CustomModal
      title="既存顧客から選択"
      modalType="registerModal"
      openModal={open}
      handleCloseModal={onClose}
      addTitleBorder="true"
      wide
    >
      <Box className="registerModalBody">
        <Box sx={{ mt: 1 }}>
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Box flex={1} maxWidth="360px">
              <Box
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              >
                <CustomTextInput
                  label=""
                  name="q"
                  placeholder="氏名・連絡先で絞り込む"
                  register={register as any}
                  errors={{}}
                  inputWidth="100%"
                  showSearchIconStart
                  search
                  hasSpacing={false}
                />
              </Box>
            </Box>
            <CustomButton
              label="検索"
              buttonCategory="search"
              onClick={handleSearch}
            />
          </Stack>

          <CustomTable
            key={searchQuery}
            rows={rows}
            columns={columns}
            isLoading={loading}
            outlined={false}
            minHeight="auto"
            noDataMessage="顧客が登録されていません"
          />
        </Box>
      </Box>
      <Box className="registerModalFooter">
        <CustomButton
          label="閉じる"
          buttonCategory="cancel"
          onClick={onClose}
        />
      </Box>
    </CustomModal>
  );
};

export default CustomerSelectModal;
